import type { SkillSummary } from "@tabbio/types";
import {
  filterAndSortSkills,
  type SkillCountry,
  type SkillLevel,
} from "../../skills/skill-taxonomy";
import { cvSkillsForSource, type CreateCvSource } from "./create-cv-machine";
export { localizedCvSkillCopy } from "../../skills/cv-skill-localization";

export type CvSkillCountryFilter = "all" | SkillCountry;
export type CvSkillLanguageFilter = "any" | "en" | "ar" | "bi";

export interface CvSkillPickerFilters {
  country: CvSkillCountryFilter;
  level: SkillLevel;
  language: CvSkillLanguageFilter;
}

export const DEFAULT_CV_SKILL_PICKER_FILTERS: CvSkillPickerFilters = {
  country: "all",
  level: "all",
  language: "any",
};

export const CV_SKILL_COUNTRY_FILTERS: readonly SkillCountry[] = [
  "uae",
  "ksa",
  "kwt",
  "qat",
  "bhr",
  "omn",
  "gulf",
];

export const CV_SKILL_LEVEL_FILTERS: readonly Exclude<SkillLevel, "all">[] = [
  "intern",
  "fresh",
  "mid",
  "exec",
];

export const CV_SKILL_LANGUAGE_FILTERS: readonly Exclude<
  CvSkillLanguageFilter,
  "any"
>[] = ["en", "ar", "bi"];

export function activeCvSkillFilterCount(filters: CvSkillPickerFilters): number {
  return Number(filters.country !== "all") +
    Number(filters.level !== "all") +
    Number(filters.language !== "any");
}

/**
 * One platform-neutral pipeline for the native bottom sheet and web dialog.
 * Source compatibility is applied first, followed by the three CV-creation
 * filters and finally the free-text search.
 */
export function filterCvSkillsForPicker(
  skills: readonly SkillSummary[],
  source: CreateCvSource | null,
  filters: CvSkillPickerFilters,
  query: string,
): SkillSummary[] {
  const fitting = cvSkillsForSource(skills, source);
  const filtered = filterAndSortSkills(fitting, {
    sort: "top",
    country: filters.country,
    level: filters.level,
    language: filters.language,
    category: "all",
  });
  const term = query.trim().toLowerCase();
  if (!term) return filtered;

  return filtered.filter((skill) =>
    [
      skill.name,
      skill.nameAr,
      skill.skillId,
      skill.description,
      skill.descriptionAr,
    ]
      .filter(Boolean)
      .some((value) => value!.toLowerCase().includes(term)),
  );
}
